// src/components/Budget/ConvertToSharedDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSharedCategory } from '../../contexts/SharedCategoryContext';
import { useTranslation } from 'react-i18next';

export default function ConvertToSharedDialog({ open, onClose, category, onSuccess }) {
  const { t } = useTranslation();
  const { convertExistingCategory, fetchSharedCategories } = useSharedCategory();
  const [budgetMode, setBudgetMode] = useState('shared');
  const [transparencyLevel, setTransparencyLevel] = useState('full');
  const [sharedLimit, setSharedLimit] = useState(0);
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const handleConvert = async () => {
    if (!category) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await convertExistingCategory(
        category.name,
        budgetMode,
        transparencyLevel,
        sharedLimit
      );
      
      setInviteCode(result.inviteCode);
      
      // Refresh shared categories list
      await fetchSharedCategories();
      
      // Notify parent to refresh user preferences
      if (onSuccess) {
        onSuccess();
      }
      
      // Don't close yet - show invite code
    } catch (error) {
      console.error('Error converting category:', error);
      setError(error.message || 'Failed to convert category. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode);
      setCopySuccess(true);
    }
  };
  
  const handleClose = () => {
    // Reset state when closing
    const hadInviteCode = !!inviteCode;
    setInviteCode(null);
    setError(null);
    setBudgetMode('shared');
    setTransparencyLevel('full');
    setSharedLimit(0);
    setCopySuccess(false);
    onClose();
    
    // If we successfully converted, refresh the page to show updated categories
    if (hadInviteCode) {
      window.location.reload();
    }
  };
  
  if (!category) return null;
  
  // Show invite code after successful conversion
  if (inviteCode) {
    return (
      <>
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🎉 {t('Category Converted!')}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('Your category is now shared. Share this code with others:')}
            </Alert>
            
            <Box sx={{ 
              textAlign: 'center', 
              my: 3, 
              p: 3, 
              bgcolor: 'grey.100',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'success.main'
            }}>
              <Typography 
                variant="h3" 
                fontWeight="bold" 
                letterSpacing={4}
                sx={{ 
                  color: 'text.primary',
                  fontFamily: 'monospace'
                }}
              >
                {inviteCode}
              </Typography>
            </Box>
            
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyInviteCode}
              sx={{ mb: 2 }}
            >
              {t('Copy Invite Code')}
            </Button>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              💡 {t('Tip: Anyone with this code can join and collaborate on this category.')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained">
              {t('Done')}
            </Button>
          </DialogActions>
        </Dialog>
        
        <Snackbar
          open={copySuccess}
          autoHideDuration={2000}
          onClose={() => setCopySuccess(false)}
          message={t('Invite code copied to clipboard!')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </>
    );
  }
  
  // Show conversion settings
  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('Share')} "{category?.name}"
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Convert this category to a shared category. All existing data will be preserved.')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('Budget Mode')}</InputLabel>
            <Select
              value={budgetMode}
              onChange={(e) => setBudgetMode(e.target.value)}
              label={t('Budget Mode')}
              disabled={loading}
            >
              <MenuItem value="shared">
                <Box>
                  <Typography variant="body1">
                    {t('Shared Limit')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Everyone contributes to one budget')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="individual">
                <Box>
                  <Typography variant="body1">
                    {t('Individual Limits')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Each person has their own limit')}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          
          {budgetMode === 'shared' && (
            <TextField
              fullWidth
              label={t('Shared Budget Limit')}
              type="number"
              value={sharedLimit}
              onChange={(e) => setSharedLimit(Number(e.target.value))}
              sx={{ mb: 2 }}
              disabled={loading}
              helperText={t('Total budget that all members will share')}
              inputProps={{ min: 0, step: 1 }}
            />
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('Transparency')}</InputLabel>
            <Select
              value={transparencyLevel}
              onChange={(e) => setTransparencyLevel(e.target.value)}
              label={t('Transparency')}
              disabled={loading}
            >
              <MenuItem value="full">
                <Box>
                  <Typography variant="body1">
                    {t('Full')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Everyone sees all transactions')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="private">
                <Box>
                  <Typography variant="body1">
                    {t('Private')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Only see your own transactions')}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          
          <Alert severity="info">
            {t('Your existing transactions and budgets will be preserved.')}
          </Alert>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConvert}
            disabled={loading || (budgetMode === 'shared' && sharedLimit <= 0)}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? t('Converting...') : t('Share Category')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
